import { ArrowUpRight, Project, Target } from '@griply/assets';
import { GoalType } from '@griply/types';
import {
  useCreateSubGoalAllowed,
  useCreateUnlimitedGoalsAllowed,
} from 'features/goals';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import {
  useOpenCreateGoalDialog,
  useOpenCreateProjectDialog,
} from 'shared/contexts/goal-form';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { ID } from 'shared/types/id';

const GOALS_HELP_URL =
  'https://griply.app/help/get-started/creating-planning-your-first-goals';

export type GoalDetailRoadmapAddMenuProps = {
  parentIds: ID[];
  onClose?: () => void;
};

export const GoalDetailRoadmapAddMenu: React.FC<
  GoalDetailRoadmapAddMenuProps
> = ({ parentIds, onClose }) => {
  const { t } = useTranslation();
  const { allowed: createSubGoalAllowed } = useCreateSubGoalAllowed();
  const { allowed: createProjectAllowed } = useCreateUnlimitedGoalsAllowed(
    GoalType.Project,
  );
  const openPremiumDialog = useOpenPremiumDialog();
  const openCreateGoalDialog = useOpenCreateGoalDialog();
  const openCreateProjectDialog = useOpenCreateProjectDialog();

  const close = () => onClose?.();

  const onCreateSubgoal = () => {
    close();
    if (!createSubGoalAllowed) {
      openPremiumDialog();
      return;
    }
    openCreateGoalDialog({ parentIds });
  };

  const onCreateProject = () => {
    close();
    if (!createProjectAllowed) {
      openPremiumDialog();
      return;
    }
    openCreateProjectDialog({ parentIds });
  };

  const onLearnMore = () => {
    close();
    window.open(GOALS_HELP_URL, '_blank', 'noreferrer');
  };

  return (
    <>
      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onCreateSubgoal}
            isPremium={!createSubGoalAllowed}
            start={<Icon icon={Target} />}
            subtitle={t('goal-detail.roadmap.add-menu.subgoal.subtitle')}
          >
            {t('goal-detail.roadmap.add-menu.subgoal.title')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onCreateProject}
            isPremium={!createProjectAllowed}
            start={<Icon icon={Project} />}
            subtitle={t('goal-detail.roadmap.add-menu.project.subtitle')}
          >
            {t('goal-detail.roadmap.add-menu.project.title')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
      <PopupMenuList>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onLearnMore}
            start={<Icon icon={ArrowUpRight} />}
            subtitle={t('goal-detail.roadmap.add-menu.learn-more.subtitle')}
          >
            {t('goal-detail.roadmap.add-menu.learn-more.title')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </>
  );
};
